import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Form,
  Col,
  Container,
  Image,
  ListGroup,
  Row,
  Nav,
  Spinner,
} from "react-bootstrap";
import BreadcrumbSection from "components/BreadcrumbSection";
import IMAGES from "constants/images";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import CommonModal from "components/CommonModal";
import TextInput from "components/TextInput";
import VendorSidebar from "inc/VendorSidebar";
import {
  getIndustriesList,
  getCompanySizesList,
  getMyProfileDetails,
  updateProfile,
  fileUpload,
  getRegionsList,
} from "api/authService";
import { toast } from "react-toastify";
import { useAuth } from "context/UserContext";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useFetchProfile } from "hooks/useFetchProfile";
import ChangePasswordModal from "pages/profile/myprofile/ChangePasswordModal";
import { Sentry } from "react-activity";
import FollowFollowing from "pages/profile/myprofile/FollowFollowing";
const schema = yup.object().shape({
  firstName: yup
    .string()
    .required("First Name is required")
    .max(10, "First Name cannot exceed 10 characters"),
  lastName: yup
    .string()
    .required("Last Name is required")
    .max(10, "Last name cannot exceed 10 characters"),
  email: yup.string().email("Invalid email").required("Email is required"),
  region: yup.string(),
  title: yup.string(),
  companyName: yup.string(),
  companyEmail: yup.string().email("Invalid email"),
  industry: yup.string(),
  companySize: yup.string(),
  yearFounded: yup.string(),
  hqLocation: yup.string(),
  linkedInProfileUrl: yup.string().url("Invalid URL"),
  twitterProfileUrl: yup.string().url("Invalid URL"),
  companyWebsiteUrl: yup.string().url("Invalid URL"),
  description: yup.string(),
  companyDescription: yup.string(),
  companyLogo: yup.string(),
});

const breadcrumbMap = {
  "/vendor/myprofile": [{ title: "My Profile" }],
  "/vendor/myprofile/edit": [{ title: "My Profile" }, { title: "Edit" }],
  "/vendor/products": [{ title: "Products" }],
  "/vendor/addproduct": [
    { title: "Products", link: "/vendor/products" },
    { title: "Add Product" },
  ],
  "/vendor/editproduct": [
    { title: "Products", link: "/vendor/products" },
    { title: "Edit Product" },
  ],
  "/vendor/mysubscription": [
    { title: "My Profile", link: "/vendor/myprofile" },
    { title: "My Subscription" },
  ],
  "/vendor/dispute-management": [
    { title: "My Profile", link: "/vendor/myprofile" },
    { title: "Dispute Management" },
  ],
  "/vendor/my-badges": [
    { title: "My Profile", link: "/vendor/myprofile" },
    { title: "My Badges" },
  ],
  "/vendor/notifications": [
    { title: "My Profile", link: "/vendor/myprofile" },
    { title: "Notifications" },
  ],
};

const ProfilePage = ({ children }) => {
  const fetchCalledRef = useRef(false);
  const [industries, setIndustries] = useState([]);
  const location = useLocation();
  const breadcrumbs = breadcrumbMap[location.pathname] || [
    { title: "Profile" },
  ];
  const [companySizes, setCompanySizes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviewCompany, setImagePreviewCompany] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const {
    login: loginUser,
    user,
    showFollowers,
    setShowFollowers,
    setFollowTab,
    followTab,
  } = useAuth();
  const navigate = useNavigate();
  // const [activeTab1, setActiveTab1] = useState("Followers");
  const { profile, loading, refetchProfile } = useFetchProfile();
  const [uiLoading, setUiLoading] = useState(true);
  // const [currentUser,setCurrentUser]=useState(null)
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const {
    handleSubmit,
    register,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const fetchData = async () => {
    try {
      const [industriesRes, companySizesRes, regionsRes] = await Promise.all([
        getIndustriesList(),
        getCompanySizesList(),
        getRegionsList(),
      ]);

      // const profileRes = await getMyProfileDetails();

      setIndustries(industriesRes.body.data);
      setCompanySizes(companySizesRes.body.data);
      setRegions(regionsRes.body.data);
      // const profile = profileRes.body;
      // setCurrentUser(profile)
      // set default values
    } catch (err) {
      console.error("Error loading profile form:", err);
    }
  };

  useEffect(() => {
    if (!fetchCalledRef.current && !profile) {
      fetchCalledRef.current = true;
      fetchData();
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      const findIndustry = industries.find((v) => v._id === profile.industry);
      const findComanySize = companySizes.find(
        (v) => v._id === profile.companySize
      );
      const findRegion = regions.find((v) => v._id === profile.region);

      setValue("firstName", profile?.firstName);
      setValue("lastName", profile?.lastName);
      setValue("email", profile?.email);
      setValue("region", findRegion?.title || "");
      setValue("title", profile?.title || "");
      setValue("companyName", profile?.companyName || "");
      setValue("companyEmail", profile?.companyEmail || "");
      setValue("industry", findIndustry?.title || "");
      setValue("companySize", findComanySize?.title || "");
      setValue("yearFounded", profile?.yearFounded || "");
      setValue("hqLocation", profile?.hqLocation || "");
      setValue("linkedInProfileUrl", profile?.linkedInProfileUrl || "");
      setValue("twitterProfileUrl", profile?.twitterProfileUrl || "");
      setValue("companyWebsiteUrl", profile?.companyWebsiteUrl || "");
      setValue("description", profile?.description || "");
      setValue("companyDescription", profile?.companyDescription || "");
      setImagePreview(profile?.profileUrl || IMAGES.User2_Img);
      setImagePreviewCompany(profile?.companyLogo || IMAGES.User2_Img);
      setUiLoading(false);
    }
  }, [profile, industries, companySizes, regions]);

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      return;
    }
    setImageFile(file, type);
    if (type === "profile") {
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
      }
    } else if (type === "company") {
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => setImagePreviewCompany(reader.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCloseModal = () => {
    setShowPasswordModal(false);
  };

  const onSubmit = async (data) => {
    try {
      setUpdateLoading(true);
      let uploadedImageUrl = null;
      let uploadedImageUrlCompany = null;
      if (imageFile && imageFile.profile) {
        const res = await fileUpload(imageFile.profile);
        uploadedImageUrl = res.body[0];
      }
      if (imageFile && imageFile.company) {
        const res = await fileUpload(imageFile.company);
        uploadedImageUrlCompany = res.body[0];
      }

      const findIndustry = industries.find((v) => v.title === data.industry);
      const findComanySize = companySizes.find(
        (v) => v.title === data.companySize
      );
      const findRegion = regions.find((v) => v.title === data.region);
      const payload = {
        ...data,
        profileUrl: uploadedImageUrl || imagePreview,
        companyLogo: uploadedImageUrlCompany || imagePreviewCompany,
        industry: findIndustry?._id || null,
        companySize: findComanySize?._id || null,
        region: findRegion?._id || null,
      };

      const response = await updateProfile(payload);
      if (response.success === true && response.code === 200) {
        const token = localStorage.getItem("authToken");
        loginUser(response.body, token); // store in context
        toast.success(response.message);
        refetchProfile();
      }
      setUpdateLoading(false);
      //   onEdit(); // close modal or show toast
    } catch (err) {
      setUpdateLoading(false);
      toast.error("Failed to load profile data");
      console.error("Update failed", err);
    }
  };

  // Followers List
  const [followers, setFollowers] = useState([
    {
      id: 1,
      name: "Amelia Zajac",
      followers: "1.2k",
      image: IMAGES.User1_IMG,
    },
    {
      id: 2,
      name: "Juan Rodríguez",
      followers: "1.2k",
      image: IMAGES.User2_Img,
    },
    {
      id: 3,
      name: "Emmanuel Pérez",
      followers: "1.2k",
      image: IMAGES.User3_Img,
    },
    {
      id: 4,
      name: "Amelia Zajac",
      followers: "1.2k",
      image: IMAGES.User1_IMG,
    },
    {
      id: 5,
      name: "Juan Rodríguez",
      followers: "1.2k",
      image: IMAGES.User2_Img,
    },
    {
      id: 6,
      name: "Emmanuel Pérez",
      followers: "1.2k",
      image: IMAGES.User3_Img,
    },
    {
      id: 7,
      name: "Juan Rodríguez",
      followers: "1.2k",
      image: IMAGES.User2_Img,
    },
  ]);

  // Following List
  const [following, setFollowing] = useState([
    {
      id: 1,
      name: "Sara Adébáyọ̀",
      followers: "1.2k",
      image: IMAGES.User2_Img,
    },
    {
      id: 2,
      name: "Slack",
      followers: "1.2k",
      image: IMAGES.Follow1_IMG,
    },
    {
      id: 3,
      name: "Hugo Silva",
      followers: "1.2k",
      image: IMAGES.Follow2_IMG,
    },
    {
      id: 4,
      name: "Sara Adébáyọ̀",
      followers: "1.2k",
      image: IMAGES.User2_Img,
    },
    {
      id: 5,
      name: "Slack",
      followers: "1.2k",
      image: IMAGES.Follow1_IMG,
    },
    {
      id: 6,
      name: "Hugo Silva",
      followers: "1.2k",
      image: IMAGES.Follow2_IMG,
    },
  ]);
  const handleRemove = (id) => {
    setFollowers(followers.filter((user) => user.id !== id));
    setFollowing(following.filter((user) => user.id !== id));
  };
console.log('breadcrumbs', breadcrumbs)
  return (
    <React.Fragment>
      {/* BreadCrumb Section */}
      <BreadcrumbSection breadcrumbs={breadcrumbs}  setShowFollowers={setShowFollowers}/>

      <section className="software_section py-5">
        <Container>
          <Row>
            <VendorSidebar
              showFollowers={showFollowers}
              setShowFollowers={setShowFollowers}
              setFollowTab={setFollowTab}
            />
            {/* Profile Details Form */}
            <Col style={{ width: "100%" }}>
              <div className="right_profile_style">
                <div className="tab_1_style">
                  {/* Tabs Hide SHow */}
                  {showFollowers ? (
                    <FollowFollowing
                      followers={followers}
                      following={following}
                      onRemove={handleRemove}
                      followTab={followTab}
                      setShowFollowers={setShowFollowers}
                    />
                  ) : (
                    <Outlet />
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <ChangePasswordModal
          show={showPasswordModal}
          onClose={handleCloseModal}
        />
      </section>
    </React.Fragment>
  );
};

export default ProfilePage;
